/* ==========================================================================
   GLOWSTONE — Motion
   One Lenis instance driven by the GSAP ticker, scoped scroll reveals,
   and the transformation sequence. Respects prefers-reduced-motion and
   the Shopify theme editor.
   ========================================================================== */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var hasGsap = typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined';
  var designMode = window.Shopify && window.Shopify.designMode;

  /* ------------------------------------------------------------------
     Header scrolled state — functional, runs regardless of motion prefs
  ------------------------------------------------------------------ */
  var scrolled = false;
  function updateScrolledState() {
    var next = window.scrollY > 24;
    if (next !== scrolled) {
      scrolled = next;
      document.body.classList.toggle('gs-scrolled', scrolled);
    }
  }
  window.addEventListener('scroll', updateScrolledState, { passive: true });
  updateScrolledState();

  if (reducedMotion.matches || !hasGsap) {
    document.documentElement.classList.add('gs-motion-off');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('gs-motion-ready');

  /* ------------------------------------------------------------------
     Lenis — single instance, GSAP ticker only. Off in the theme editor.
  ------------------------------------------------------------------ */
  var lenis = null;
  if (!designMode && typeof window.Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 1.05,
      smoothWheel: true,
      touchMultiplier: 1.4,
    });

    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    /* Keep native anchors usable */
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link) return;
      var id = link.getAttribute('href');
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: -96 });
      history.pushState(null, '', id);
    });
  }

  if ('scrollRestoration' in history) history.scrollRestoration = 'auto';

  gsap.defaults({ ease: 'power3.out', duration: 0.65 });

  /* ------------------------------------------------------------------
     Reveals — opt-in via data-gs-reveal / data-gs-reveal-group
  ------------------------------------------------------------------ */
  function initReveals(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-gs-reveal]').forEach(function (el) {
      if (el.dataset.gsRevealInit) return;
      el.dataset.gsRevealInit = 'true';
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          el.classList.add('gs-revealed');
        },
      });
    });

    root.querySelectorAll('[data-gs-reveal-group]').forEach(function (group) {
      if (group.dataset.gsRevealInit) return;
      group.dataset.gsRevealInit = 'true';
      var items = group.children;
      gsap.set(items, { opacity: 0, y: 24 });
      ScrollTrigger.create({
        trigger: group,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          gsap.to(items, { opacity: 1, y: 0, stagger: 0.08, duration: 0.6, overwrite: 'auto' });
        },
      });
    });
  }

  /* ------------------------------------------------------------------
     Transformation sequence — desktop only, short pin, three states
  ------------------------------------------------------------------ */
  var transformContexts = {};

  function initTransformation(section) {
    var stage = section.querySelector('[data-gs-transform-stage]');
    if (!stage || stage.dataset.gsInit) return;
    stage.dataset.gsInit = 'true';

    var mm = gsap.matchMedia();
    mm.add('(min-width: 990px)', function () {
      var relief = stage.querySelector('.gs-transform__frame--relief');
      var lit = stage.querySelector('.gs-transform__frame--lit');
      var caption = section.querySelector('[data-gs-transform-caption]');
      var labels = ['Original', 'Sculpted', 'Illuminated'];
      try {
        labels = JSON.parse(section.dataset.gsLabels || '[]').concat(labels).slice(0, 3);
      } catch (e) {
        /* fall back to defaults */
      }

      var timeline = gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top top+=96',
          end: '+=140%',
          pin: true,
          scrub: 0.6,
          anticipatePin: 1,
          onUpdate: function (self) {
            if (!caption) return;
            var index = self.progress < 0.33 ? 0 : self.progress < 0.72 ? 1 : 2;
            if (caption.textContent !== labels[index]) caption.textContent = labels[index];
          },
        },
      });

      timeline
        .to(relief, { opacity: 1, duration: 0.4 }, 0.18)
        .to(lit, { opacity: 1, duration: 0.45 }, 0.62)
        .fromTo(stage, { scale: 0.985 }, { scale: 1, duration: 0.9, ease: 'none' }, 0);

      return function () {
        timeline.scrollTrigger && timeline.scrollTrigger.kill();
        timeline.kill();
      };
    });

    transformContexts[sectionKey(section)] = mm;
  }

  function sectionKey(section) {
    var wrapper = section.closest('.shopify-section');
    return wrapper ? wrapper.id : 'gs-' + Math.abs(hashString(section.className));
  }

  function hashString(value) {
    var hash = 0;
    for (var i = 0; i < value.length; i++) hash = (hash << 5) - hash + value.charCodeAt(i);
    return hash;
  }

  /* ------------------------------------------------------------------
     Init / editor lifecycle
  ------------------------------------------------------------------ */
  function initAll(scope) {
    initReveals(scope);
    (scope || document).querySelectorAll('[data-gs-transform]').forEach(initTransformation);
    ScrollTrigger.refresh();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    initAll(document);
    /* Recalculate once media has finished loading */
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var key = event.target.id;
    if (transformContexts[key]) {
      transformContexts[key].revert();
      delete transformContexts[key];
    }
    ScrollTrigger.getAll().forEach(function (trigger) {
      if (trigger.trigger && !document.body.contains(trigger.trigger)) trigger.kill();
    });
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:reorder', function () {
    ScrollTrigger.refresh();
  });

  reducedMotion.addEventListener('change', function (event) {
    if (event.matches) window.location.reload();
  });
})();
